import React from 'react';
import { Users, Target, Heart, Sparkles, Wand2, Bot, MessageSquare, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const About = () => {
  const navigate = useNavigate();
  const values = [
    {
      icon: <Users className="w-8 h-8 text-pink-400" />,
      title: "User-Centric Innovation",
      description: "We believe in creating AI solutions that truly understand and adapt to individual user needs, making technology work for people, not the other way around."
    },
    {
      icon: <Target className="w-8 h-8 text-pink-400" />,
      title: "Purposeful Intelligence",
      description: "Every feature we develop is designed with a clear purpose: to solve real challenges faced by professionals and businesses in their daily operations."
    },
    {
      icon: <Heart className="w-8 h-8 text-pink-400" />,
      title: "Authentic Assistance",
      description: "We're committed to creating AI sidekicks that complement human capabilities rather than replace them, fostering genuine collaborative relationships."
    },
    {
      icon: <Sparkles className="w-8 h-8 text-pink-400" />,
      title: "Continuous Evolution",
      description: "We're dedicated to constantly improving our AI technology to better serve our users' evolving needs and challenges."
    }
  ];

  const features = [
    {
      icon: <Wand2 className="w-12 h-12 text-emerald-500" />,
      title: "Auto-Generated Prompts",
      description: "Smart prompt generation that adapts to your specific needs and context, making AI interactions more natural and effective."
    },
    {
      icon: <Bot className="w-12 h-12 text-emerald-500" />,
      title: "Custom Sidekick Creation",
      description: "Design your own AI assistant with specialized knowledge and capabilities tailored to your daily tasks and industry."
    },
    {
      icon: <MessageSquare className="w-12 h-12 text-emerald-500" />,
      title: "Intelligent Conversations",
      description: "Natural dialogue system that learns your preferences and communication style over time."
    },
    {
      icon: <Settings className="w-12 h-12 text-emerald-500" />,
      title: "Workflow Integration",
      description: "Seamlessly integrate your AI sidekick into existing tools and processes for enhanced productivity."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="text-center py-20">
        <h1 className="text-4xl font-bold text-white mb-6">
          Empowering Professionals with<br />
          <span className="text-pink-500">Intelligent Assistance</span>
        </h1>
        <p className="text-gray-300 max-w-2xl mx-auto mb-8">
          At Sidekick AI, we're on a mission to revolutionize how professionals work by providing 
          intelligent, adaptable AI assistance that understands and grows with your unique needs.
        </p>
        <div className="flex justify-center gap-4 h-42">
          <img 
            src="/image/ai-office.jpg"
            alt="Team"
            className="rounded-lg"
          />
        </div>
      </section>

      {/* Vision Section */}
      <section className="bg-gray-200 py-16 -mx-10 md:-mx-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Our Vision</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-700">
            <p className="leading-relaxed">
              We believe that the future of work lies in the seamless collaboration between humans and AI. By breaking down the traditional barriers between technology and human expertise, we're creating an ecosystem where professionals from every field can harness the power of advanced AI to enhance their capabilities, streamline their workflows, and unlock new possibilities in their work. Our AI sidekicks are designed to complement rather than replace human intelligence, offering insights, automating routine tasks, and providing creative solutions while leaving the critical thinking and decision-making in your hands.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-white mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="flex gap-4 bg-white bg-opacity-10 p-6 rounded-lg">
                <div className="mt-1">{value.icon}</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-white">{value.title}</h3>
                  <p className="text-gray-300">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-200 py-16 -mx-10 md:-mx-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">Powerful Features</h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Create your perfect AI assistant with our innovative features designed to enhance your daily workflow
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
                <div className="flex justify-center mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-center py-16">
        <h2 className="text-3xl font-bold text-white mb-6">
          Ready to Experience the Future of Work?
        </h2>
        <p className="text-gray-300 max-w-2xl mx-auto mb-8">
          Join thousands of professionals who are already transforming their work with Sidekick AI.
        </p>
        <button 
        onClick={() => navigate('/feature')}
        className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-8 rounded-md transition-colors">
          Get Started Today
        </button>
      </section>
    </div>
  );
};

export default About;